import React, { useEffect } from "react";
import TableDragSelect from "react-table-drag-select";
import PropTypes from "prop-types";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { Tooltip, Box } from "@material-ui/core";
import moment from "moment";
import "moment/dist/locale/ru";
import Popover from "../Popover/Popover.jsx";
import css from "../../../../sass/grid_style.module.scss";
import {
    monthNames,
    weekDays,
    MATERIAL_UI_PROPS,
    STATE_DATE_FORMAT,
} from "../../lib/data";
import {
    getWeekDayNum,
    showWeekDay,
    turnNumbertoObjkey,
} from "../../lib/helperFuncs.jsx";

const Grid = (props) => {
    useEffect(() => {
        createMainTable(
            props.month,
            props.cells,
            props.gridValues,
            props.auxData,
            props.onMonthSelect,
            props.onSelect,
            props.onMonthChange,
            weekDays,
            props.openLeftMenu,
        );
    }, [props.month]);

    const mainTable = createMainTable(
        props.month,
        props.cells,
        props.gridValues,
        props.auxData,
        props.onMonthSelect,
        props.onSelect,
        props.onMonthChange,
        weekDays,
        props.openLeftMenu,
    );

    return <>{mainTable}</>;
};

// Helper functions
/**
 * Creates array of dates of picked month
 *
 * @param {Object} gridValues
 * @param {Array} auxData
 *
 * @returns {Array}
 */

const createDatesArr = (gridValues, auxData) => {
    const datesArr = [];
    const firstLineGridValue = gridValues[turnNumbertoObjkey(auxData[0].id)];

    for (let line in firstLineGridValue) {
        const momentDatesArr = moment(
            firstLineGridValue[line].date,
            STATE_DATE_FORMAT,
        );

        datesArr.push(momentDatesArr);
    }

    datesArr.unshift("placeholder");

    return datesArr;
};

/**
 * Creates first column with month name, year and room type names
 *
 * @param {moment} month
 * @param {Array} auxData
 * @param {Function} onMonthChange
 *
 * @returns {Array}
 */

const createFirstCol = (month, auxData, onMonthChange) => {
    const monthNum = month.format("M");
    const pickedYear = month.format("YYYY");
    const pickedMonth = monthNames[monthNum - 1];

    const [arrowBackIcon, arrowForwardIcon] = createMonthChangerIcons(
        month,
        onMonthChange,
    );

    const header = [
        <Box key="month-header">
            <Box display="flex" justifyContent="space-around">
                {arrowBackIcon}
                {arrowForwardIcon}
            </Box>
            <Box>{pickedMonth} </Box>
            <Box>{pickedYear}</Box>
        </Box>,
    ];

    const otherRows = auxData.map((room) => {
        const row = [];
        row.push({
            roomName: room["name"],
            roomId: room["id"],
            active: room["active"],
            quotas_from_asu: room["quotas_from_asu"],
            quotas_from_asu_limits: room["quotas_from_asu_limits"],
        });
        return row;
    });

    const firstCol = [header, ...otherRows];
    return firstCol;
};

/**
 * Creates back and forward arrow icons for table
 *
 * @param {moment} month
 * @param {Function} onMonthChange
 */

const createMonthChangerIcons = (month, onMonthChange) => {
    const currentMonth = moment();
    const pickedMonthNum = parseInt(month.format("M")) - 1;
    const yearAhead = moment().add(10, "month");

    const backIcon =
        month >= currentMonth ? (
            <Tooltip title="Вернуться к предыдущему месяцу">
                <ArrowBackIcon
                    fontSize={MATERIAL_UI_PROPS.sizes.small}
                    className="arrow-month-icon"
                    onClick={() => {
                        onMonthChange(pickedMonthNum - 1);
                    }}
                />
            </Tooltip>
        ) : null;

    const forwardIcon =
        month < yearAhead ? (
            <Tooltip title="Перейти на следующий месяц">
                <ArrowForwardIcon
                    fontSize={MATERIAL_UI_PROPS.sizes.small}
                    className="arrow-month-icon"
                    onClick={() => {
                        onMonthChange(pickedMonthNum + 1);
                    }}
                />
            </Tooltip>
        ) : null;

    return [backIcon, forwardIcon];
};

/**
 * Gets data about available and reserved rooms from props.gridValues and puts it in a cell
 *
 * @param {Object} arrDate
 * @param {Number} roomTypeId
 * @param {Object} gridValues
 *
 * @returns {Object}
 */

const getAvailableRoomsData = (gridValue) => {
    const amount = gridValue.amount;
    const freeUpgradeAmount = gridValue.free_upgrade_amount;
    const reserved = gridValue.reserved;
    const result = gridValue.result;
    const close = gridValue.close;
    const amountAsu = gridValue.amount_asu || 0;
    const overbookingAmount = gridValue.overbooking_amount;

    return {
        key: `${amount}/${reserved}/${result.success}`,
        vacant: amount,
        reserved: reserved,
        result: result,
        close: close,
        amountAsu,
        overbookingAmount,
        freeUpgradeAmount
    };
};

/**
 * Main function. Creates Quotas grid
 *
 * @param {Array} cells
 * @param {moment} month
 * @param {Object} gridValues
 * @param {Array} auxData
 * @param {Function} onMonthSelect
 * @param {Function} onCellSelect
 * @param {Function} onMonthChange
 * @param {Array} weekDays
 */

const createTable = (
    cells,
    month,
    gridValues,
    auxData,
    onMonthSelect,
    onCellSelect,
    onMonthChange,
    weekDays,
    openLeftMenu,
) => {
    const firstColumn = createFirstCol(month, auxData, onMonthChange);
    const arrDates = createDatesArr(gridValues, auxData);
    const currDate = moment().subtract(1, "day");
    const widthFromMenu = openLeftMenu ? 237 : 48;

    const table = cells.map((row, ir) => {
        const rowData =
            ir === 0
                ? row.map((_, idx) => {
                      let weekDayNum;
                      if (idx !== 0) {
                          weekDayNum = getWeekDayNum(arrDates[idx]);
                      }

                      const cell =
                          idx === 0 ? (
                              <td
                                  key={`header-month-${firstColumn[idx][idx]}`}
                                  disabled
                                  className=" first-column month-name"
                                  style={{ left: "0" }}
                              >
                                  {firstColumn[idx][idx]}
                              </td>
                          ) : (
                              <td
                                  style={{
                                      display:
                                          arrDates[idx] < currDate
                                              ? "none"
                                              : "table-cell",
                                  }}
                                  key={`header-month-${arrDates[idx].format(
                                      "DD/MM",
                                  )}`}
                                  disabled
                                  className=" top-row"
                                  onClick={onMonthSelect}
                              >
                                  <div>{arrDates[idx].format("DD")}</div>
                                  <div>
                                      {showWeekDay(
                                          weekDayNum == 0 ? 6 : weekDayNum - 1,
                                          weekDays,
                                      )}
                                  </div>
                              </td>
                          );
                      return cell;
                  })
                : row.map((_, i) => {
                      const {
                          key,
                          vacant,
                          reserved,
                          result,
                          close,
                          amountAsu,
                          overbookingAmount,
                          freeUpgradeAmount
                      } =
                          i !== 0
                              ? getAvailableRoomsData(
                                    gridValues[
                                        turnNumbertoObjkey(
                                            firstColumn[ir][0].roomId,
                                        )
                                    ][arrDates[i].format("D")],
                                )
                              : {};
                      let weekDayNum;
                      if (i !== 0) {
                          weekDayNum = getWeekDayNum(arrDates[i]);
                      }

                      const cell =
                          i === 0 ? (
                              <td
                                  disabled
                                  key={`roomType-${firstColumn[ir][i]["roomName"]}-${close}`}
                                  className=" first-column room-types"
                                  style={{
                                      left: widthFromMenu,
                                      paddingLeft: "7px",
                                      width: "270px",
                                  }}
                              >
                                  {firstColumn[ir][i]["roomName"]}
                                  <div>
                                      Лимит: {firstColumn[ir][i]["quotas_from_asu_limits"]}
                                  </div>
                              </td>
                          ) : arrDates[i] < currDate ||
                            !firstColumn[ir][0].active ? (
                              <td
                                  style={{ display: "none" }}
                                  key={`cell-${i}-${close}`}
                                  className=" cells-quota-disabled"
                                  disabled
                              >
                                  &Chi;
                              </td>
                          ) : (
                              <td
                                  style={{
                                      position: "relative",
                                      paddingTop: "20px",
                                      backgroundColor:
                                          vacant === null
                                              ? "#c75555"
                                              : close
                                                ? "orange"
                                                : vacant == 0
                                                  ? "pink"
                                                  : vacant < 0
                                                    ? "#1c6b72"
                                                    : "",
                                  }}
                                  key={`cell-${arrDates[i]}-${firstColumn[ir][0]["roomId"]}-${key}-${close}-${overbookingAmount}-${freeUpgradeAmount}`}
                                  className={
                                      weekDayNum === 6 || weekDayNum === 0
                                          ? "day-off"
                                          : ""
                                  }
                              >
                                  <Popover
                                      vacant={vacant}
                                      freeUpgradeAmount={freeUpgradeAmount}
                                      reserved={reserved}
                                      result={result}
                                      thisQuotas={true}
                                      amountAsu={amountAsu}
                                      overbookingAmount={overbookingAmount}
                                  />
                              </td>
                          );
                      return cell;
                  });
        return <tr key={ir}>{rowData}</tr>;
    });
    return (
        <TableDragSelect
            key={month}
            value={cells}
            onChange={(cells) => onCellSelect(cells)}
            className={css["table-drag-select"]}
        >
            {table}
        </TableDragSelect>
    );
};

/**
 * Checks data and creates a table
 *
 * @param {moment} month
 * @param {Array} cells
 * @param {Object} gridValues
 * @param {Array} auxData
 * @param {Function} onMonthSelect
 * @param {Function} onSelect
 * @param {Function} onMonthChange
 * @param {Array} weekDays
 */

const createMainTable = (
    month,
    cells,
    gridValues,
    auxData,
    onMonthSelect,
    onSelect,
    onMonthChange,
    weekDays,
    openLeftMenu,
) => {
    if (auxData) {
        const shouldBeCols =
            Object.keys(gridValues[turnNumbertoObjkey(auxData[0].id)]).length +
            1;

        const mainTable =
            shouldBeCols !== cells[0].length
                ? null
                : createTable(
                      cells,
                      month,
                      gridValues,
                      auxData,
                      onMonthSelect,
                      onSelect,
                      onMonthChange,
                      weekDays,
                      openLeftMenu,
                  );

        return mainTable;
    }
};

Grid.propTypes = {
    auxData: PropTypes.array.isRequired,
    gridValues: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    onMonthSelect: PropTypes.func.isRequired,
    month: PropTypes.object.isRequired,
    cells: PropTypes.array.isRequired,
    onMonthChange: PropTypes.func.isRequired,
};

export default Grid;
